/**
 * Public seller API (/api/v1, docs/SELLER_API_PLAN.md): what the panel's Integraciones screen and the
 * server share. The request/response schemas live with the server (server/api/v1/schemas.ts), which
 * also publishes them as /api/v1/openapi.json.
 */

export const API_SCOPES = ["listings:read", "listings:write", "orders:read", "orders:write", "webhooks:manage"] as const;
export type ApiScope = (typeof API_SCOPES)[number];

/** What a new key gets unless the seller ticks more: read only. */
export const DEFAULT_API_SCOPES: readonly ApiScope[] = ["listings:read", "orders:read"];

/** Requests per key per minute. A bulk request counts its items. */
export const API_RATE_LIMITS = { read: 600, write: 120 } as const;

export const API_KEY_NAME_MAX = 60;

/** The API's version date: in openapi.json and in every event's `api_version`. */
export const API_VERSION_DATE = "2026-10-19";

/** An API key as the panel lists it: never the key itself (only shown once, when created). */
export interface ApiKeyView {
  id: number;
  name: string;
  /** "pf_live_a1b2c3d4": enough to tell keys apart. */
  prefix: string;
  scopes: ApiScope[];
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  createdAt: string;
}

export interface ApiKeysView {
  keys: ApiKeyView[];
  /** How many the plan allows (0: the plan has no API). */
  max: number;
}

export const isApiScope = (s: unknown): s is ApiScope => typeof s === "string" && (API_SCOPES as readonly string[]).includes(s);

/** What happens in a shop that the API reports (GET /api/v1/events and webhooks). */
export const EVENT_TYPES = [
  "order.created",
  "order.payment_claimed",
  "order.confirmed",
  "order.paid",
  "order.shipped",
  "order.handed",
  "order.completed",
  "order.cancelled",
  "order.disputed",
  "order.updated",
  "order.message.created",
  "offer.created",
  "offer.accepted",
  "offer.expired",
  "listing.created",
  "listing.updated",
  "listing.stock_changed",
  "listing.sold_out",
  "listing.removed",
  "review.created",
  "want.matched",
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

export interface WebhookView {
  id: number;
  url: string;
  description: string;
  types: string[];
  active: boolean;
  failingSince: string | null;
  disabledReason: string | null;
  createdAt: string;
  lastDelivery: { status: string; at: string; code: number | null } | null;
}

export interface WebhookDeliveryView {
  id: number;
  eventId: string;
  type: string;
  status: string;
  attempt: number;
  nextAt: string | null;
  responseCode: number | null;
  responseMs: number | null;
  responseBody: string | null;
  error: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

/** One request to /api/v1, as Integraciones' log shows it. */
export interface ApiRequestView {
  at: string;
  method: string;
  path: string;
  status: number;
  ms: number;
  /** The key's name (keys are only revoked, never deleted; null only if its row was removed). */
  key: string | null;
  requestId: string;
}
